<script setup>
import { v4 as uuidv4 } from 'uuid'
import { computed } from 'vue'
import {useI18n} from "vue-i18n"

import IconDiamond from '@/components/icons/IconDiamond.vue'
import {useHelpers} from "@/useHelpers"

const props = defineProps({
  data: {
    type: Object,
    default: () => {}
  },
  modelValue: {
    type: [String],
    default: ''
  }
})

const { formatMoney, ucFirst } = useHelpers()
const { t } = useI18n()

const id = computed(() => `radio-${uuidv4()}`)

const emits = defineEmits(['update:modelValue', 'onWhatContainsClick'])

const interval = computed(() => {
  let interval = {}
  const length = props.data.length
  switch (length) {
    case 730:
      interval.short = t('two-yearly')
      interval.long = ucFirst(t('two yearly payment'))
      break
    case 365:
      interval.short = t('yearly')
      interval.long = ucFirst(t('yearly payment'))
      break
    default:
      interval.short = t('monthly')
      interval.long = ucFirst(t('monthly payment'))
      break
  }
  return interval
})

const overridenInterval = computed(() => {
  let interval = {}
  const length = props.data.overriddenLength || props.data.length
  switch (length) {
    case 730:
      interval.short = t('two years')
      interval.long = ucFirst(t('two yearly payment'))
      break
    case 365:
      interval.short = t('year')
      interval.long = ucFirst(t('yearly payment'))
      break
    default:
      interval.short = t('month')
      interval.long = ucFirst(t('monthly payment'))
      break
  }
  return interval
})

const inputValue = computed({
  get() {
    return props.modelValue
  },
  set(newValue) {
    emits('update:modelValue', newValue)
  }
})
</script>

<template>
  <div
    style="height: 100%"
    :class="{
      'pricing-page__card': true,
      'pricing-page__card--active': data.isFeatured
    }"
  >
    <div v-if="data.isFeatured" class="pricing-page__card-heading">
      <IconDiamond />
      <span class="tag">{{ $t('Favourite choice') }}</span>
    </div>

    <h5 class="pricing-page__card-header">{{ data.user_label }}</h5>

    <div class="pricing-page__card-prices">
      <div class="pricing-page__card-price pricing-page__card-price--current">
        <h3 class="pricing-page__card-price-value">{{ formatMoney(data.overriddenPrice ? data.overriddenPrice : data.price)}}</h3>
        <p class="pricing-page__card-price-label button-text">/ {{ overridenInterval.short }}</p>
      </div>
      <div
          v-if="data.originalPriceDiscount"
          class="pricing-page__card-price pricing-page__card-price--original"
      >
        <h6 class="pricing-page__card-price-value">{{ formatMoney(data.overriddenOriginalPrice ? data.overriddenOriginalPrice : data.originalPrice)}}</h6>
        <p class="pricing-page__card-price-label tag-text">-{{ data.originalPriceDiscount }} %</p>
      </div>
    </div>

    <div class="pricing-page__card-button">
      <button
        :id="id"
        @click="inputValue = data.code"
        class="button button--medium button--secondary"
        name="package-option"
      >
        {{ $t('Order interval subscription', {interval: interval.short}) }}
      </button>
      <span class="small-text">
        {{ interval.long }} {{ formatMoney(data.price)}} s DPH
        <template v-if="data.originalPrice">
          ({{$t('Shop price')}} {{ formatMoney(data.originalPrice) }} s DPH)
        </template>
      </span>
    </div>

    <ul v-if="data.info" class="pricing-page__card-offers">
      <li
        v-for="(info, key) in data.info"
        :key="`${data.id}-${key}`"
        class="pricing-page__card-offer"
      >
        <span class="pricing-page__list-marker"></span>
        <span class="basic-14">{{ info }}</span>
      </li>
    </ul>
  </div>
</template>
